import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';

import warningSvg from 'assets/icons/warning.svg';

import { Store } from 'components/common/types/Store.types';
import { Asset } from 'components/common/types/Asset.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import { FrameDetailsModal } from 'components/pages/DealWithLines/common/FrameDetailsModal/FrameDetailsModal';
import { useToggle } from 'customHooks/useToggle';
import { getDischargedFrameIdsSet } from 'components/pages/DealWithLines/common/utils';
import { dismissOOCFramesModal } from 'store/dealManagement/reducer';
import Button, { Color, Size } from 'lib/Button';
import Modal from 'lib/Modal';

interface OOCFramesModalProps {
  checkAllocate: () => void;
}

const OOCFramesModal = ({ checkAllocate }: OOCFramesModalProps): React.ReactElement => {
  const dispatch = useDispatch();
  const [showFrameListModal, setShowFrameListModal] = useState(false);
  const [dischargedAssets, setDischargedAssets] = useState<Asset[]>([]);
  const { isOpen: isOOCModalOpen, close: closeOOCModal, open: openOOCModal } = useToggle(false);

  const {
    isOOCFramesModalDismissed,
    commonDeal: {
      currentLine: { dischargedFrameIds, availability },
    },
  } = useSelector((state: Store) => state.dealManagement);

  const hasOutOfChargeEnabled = useHasFeatureAccess(FeatureFlags.OUT_OF_CHARGE);
  const prevDischargedFrameIdsRef = useRef<typeof dischargedFrameIds | null>(null);

  useEffect(() => {
    if (!hasOutOfChargeEnabled) return;

    const hasDischargedFrameIdsChanged = !isEqual(prevDischargedFrameIdsRef.current, dischargedFrameIds);
    prevDischargedFrameIdsRef.current = dischargedFrameIds;

    if (
      dischargedFrameIds?.length > 0 &&
      availability.assets.length > 0 &&
      hasDischargedFrameIdsChanged &&
      !isOOCFramesModalDismissed
    ) {
      openOOCModal();
      const dischargedIds = getDischargedFrameIdsSet(dischargedFrameIds);
      setDischargedAssets(availability.assets.filter((asset) => dischargedIds.has(asset.frameId)) as Asset[]);
    } else if (!dischargedFrameIds?.length) {
      closeOOCModal();
    }
  }, [dischargedFrameIds, hasOutOfChargeEnabled, availability.assets, isOOCFramesModalDismissed]);

  return (
    <>
      <Modal
        title="Out of Charge Frames"
        isOpen={isOOCModalOpen}
        onClose={closeOOCModal}
        icon={warningSvg}
        actionButtons={
          <>
            <Button
              color={Color.DANGER}
              size={Size.LARGE}
              onClick={() => {
                dispatch(dismissOOCFramesModal());
                closeOOCModal();
                setShowFrameListModal(true);
              }}
              label="Review Frames"
            />
            <Button
              size={Size.LARGE}
              color={Color.PRIMARY}
              onClick={() => {
                dispatch(dismissOOCFramesModal());
                closeOOCModal();
                checkAllocate();
              }}
              label="Reallocate"
            />
          </>
        }
      >
        <div className="ml-7 body-base text-neutral-950-opacity-60">
          <p className="mb-2">
            This line includes {dischargedFrameIds?.length} out of charge frames. This may affect the performance of
            this campaign.
          </p>
          <p>How would you like to proceed?</p>
        </div>
      </Modal>
      <FrameDetailsModal
        assets={dischargedAssets}
        isOpen={showFrameListModal}
        onClose={() => setShowFrameListModal(false)}
        footer={
          <div className="text-right mt-4">
            <Button size={Size.LARGE} color={Color.PRIMARY} onClick={checkAllocate} label="Reallocate" />
          </div>
        }
      />
    </>
  );
};

export default OOCFramesModal;
